<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import ProjectsApi from "@api/projects.api.js";

import { useMetadataStore } from "@store/metadata";
import FormElSelect from "../../components/form/FormElSelect.vue";

const state = useMetadataStore();
const metadata = computed(() => state.getMetaData);
const options = ref([]);

const projectsForSelect = computed(() => {
    const projects = metadata.value.projects ?? [];
    return projects;
    // return projects.filter((project) =>
    //     project.document_types?.find(
    //         (dt) => dt.id === props.document?.document_type_id
    //     )
    // );
});
const businessUnitsForSelect = ref([]);
const offeringForSelect = computed(() => {
    const offerings = metadata.value.offerings ?? metadata.value.offering ?? [];
    return offerings;
});
const businessDomainsForSelect = computed(() => {
    const domains = metadata.value.businessDomains ?? [];
    return domains;
});

const props = defineProps({
    document: {
        type: Object,
    },
    v$: {
        type: Object,
    },
    formErrors: {
        type: Object,
    },
});

const emit = defineEmits(["submit-disabled"]);

const optionSelects = reactive({
    selects: {
        tracks: [],
    },
});

// Computed property to determine if tracks should be disabled
const isTracksDisabled = computed(() => {
    // Disable if no project is selected
    if (!props.document?.project_id) {
        return true;
    }

    // Disable if project has no tracks
    if (
        !optionSelects.selects.tracks ||
        optionSelects.selects.tracks.length === 0
    ) {
        return true;
    }

    return false;
});

// Reset all fields that depend on the selected project
const resetProjectDependentFields = () => {
    optionSelects.selects.tracks = [];

    if (props.document) {
        props.document.track_id = null;
        props.document.business_unit_id = null;
        props.document.service_line_ids = [];
        props.document.offering_ids = [];
        props.document.business_domain_ids = [];
    }
};

const getProjectData = (selectedValue) => {
    // If cleared, reset all dependent fields and exit
    if (!selectedValue) {
        resetProjectDependentFields();
        businessUnitsForSelect.value = [];
        emit("submit-disabled", true);
        return;
    }

    // Clear tracks and business unit selection when project changes
    props.document.track_id = null;
    props.document.business_unit_id = null;

    ProjectsApi.get(
        { id: selectedValue },
        { embed: "businessDomains,businessUnit,serviceLines,offerings" },
        { "X-Disable-Global-Loader": true }
    ).then((response) => {
        const project = response.data.data.project;
        businessUnitsForSelect.value = project.businessUnits || [];
        if (project.businessUnits.length === 1) {
            props.document.business_unit_id = project.businessUnits[0].id;
        }
        props.document.business_domain_ids = project.business_domain_ids || [];
        props.document.service_line_ids = project.service_line_ids || [];
        props.document.offering_ids = project.offering_ids || [];
        optionSelects.selects.tracks =
            [
                { id: null, name: "Not Applicable" }, // first option
                ...project.tracks,
            ] || [];
        const canSave = !!(
            props.document?.project_id && props.document?.business_unit_id
        );
        emit("submit-disabled", !canSave);
    });
};

// If a project is already selected (edit mode), load its related data on mount
onMounted(() => {
    if (props.document?.project_id) {
        getProjectData(props.document.project_id);
    } else {
        emit("submit-disabled", true);
    }
});

// Also react if project_id is set programmatically after mount
watch(
    () => props.document?.project_id,

    (newVal, oldVal) => {
        if (newVal && newVal !== oldVal) {
            getProjectData(newVal);
            const canSave = !!(
                props.document?.project_id && props.document?.business_unit_id
            );
            emit("submit-disabled", !canSave);
        } else if (!newVal) {
            // If project is cleared via UI, reset dependent fields
            resetProjectDependentFields();
            emit("submit-disabled", true);
        }
    }
);

// React to business unit selection changes to enable/disable save
watch(
    () => props.document?.business_unit_id,
    (newVal) => {
        const canSave = !!(props.document?.project_id && newVal);
        emit("submit-disabled", !canSave);
    }
);
</script>

<template>
    <div>
        <div class="row mb-3">
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.project_name')"
                    :model="document"
                    :options="projectsForSelect"
                    :required="true"
                    clearable
                    label-field="name"
                    name="project_id"
                    placeholder="Select your Project"
                    @select="getProjectData"
                />
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.track_select')"
                    :model="document"
                    :options="optionSelects.selects.tracks"
                    :required="false"
                    :disabled="isTracksDisabled"
                    clearable
                    label-field="name"
                    name="track_id"
                    placeholder="Select a Track (If relevant)"
                />
                <small
                    v-for="error in v$.track_id.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.business_unit')"
                    :model="document"
                    :options="businessUnitsForSelect"
                    :clearable="true"
                    label-field="name"
                    name="business_unit_id"
                    required
                />
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.offering_title')"
                    :model="document"
                    :options="offeringForSelect"
                    :clearable="false"
                    label-field="name"
                    name="offering_ids"
                    :multiple="true"
                    disabled
                    readonly
                />
            </div>
        </div>
        <div class="row">
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.business_domain')"
                    :model="document"
                    :multiple="true"
                    :options="businessDomainsForSelect"
                    :clearable="false"
                    label-field="name"
                    name="business_domain_ids"
                    disabled
                    readonly
                />
            </div>
        </div>
    </div>
</template>
